import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { useTranslation } from 'react-i18next'
import { Input } from 'components/ui/Input'
import { useFormik } from 'formik'
import { formFieldProps } from 'utils/form'
import { createValidationSchema, required } from 'utils/validations'
import { UIButton } from 'components/ui/Button'
import { KeyboardEvent } from 'react'
import { InputPassword } from 'components/ui/InputPassword'
import { useModal, useModalActions } from 'providers/ModalProvider'
import { useAuthFlow, RegisterFormValues } from 'hooks/useAuthFlow'

type Props = {
  onSuccess?: () => void
}

export const RegisterModal = ({ onSuccess }: Props) => {
  const { t } = useTranslation()
  const { close } = useModal()
  const { replaceWithLogin } = useModalActions()
  const { handleRegister, isLoading } = useAuthFlow()

  const { values, errors, setFieldValue, setFieldError, handleSubmit } = useFormik<RegisterFormValues>({
    initialValues: {
      email: '',
      password: '',
      name: '',
      surname: '',
    },
    validationSchema: createValidationSchema({
      email: required,
      password: required,
      name: required,
      surname: required,
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      try {
        await handleRegister(values, () => {
          close()
          onSuccess?.()
        })
      } catch {
        // Error already handled in hook
      }
    },
  })

  const onClickAlreadyRegistered = () => {
    replaceWithLogin()
  }

  const fieldProps = formFieldProps(errors, values, setFieldError, setFieldValue)

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  return (
    <Modal onClose={close}>
      <ModalHeader title={t('register')} icon="user-plus" />
      <ModalBody className={'tw-space-y-1'}>
        <Input label={t('name')} {...fieldProps('name')} onKeyDown={onKeyDown} />
        <Input label={t('surname')} {...fieldProps('surname')} onKeyDown={onKeyDown} />
        <Input label={t('email')} {...fieldProps('email')} onKeyDown={onKeyDown} />
        <InputPassword label={t('password')} {...fieldProps('password')} onKeyDown={onKeyDown} />
        <div className={'tw-h-2'} />
        <div className={'tw-text-sm tw-underline tw-cursor-pointer'} onClick={onClickAlreadyRegistered}>
          {t('alreadyRegistered')}
        </div>
      </ModalBody>
      <ModalFooter>
        <UIButton className={'tw-w-full'} onClick={() => handleSubmit()} loading={isLoading}>
          <span className={'tw-text-sm'}>{t('register')}</span>
        </UIButton>
      </ModalFooter>
    </Modal>
  )
}
